import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Warehouse } from '@/schemas/warehouse';

interface WarehousesState {
  byId: Record<string, Warehouse>;
  allIds: string[];
  filters: {
    search?: string;
    state?: string;
  };
  selectedId: string | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: WarehousesState = {
  byId: {},
  allIds: [],
  filters: {},
  selectedId: null,
  isLoading: false,
  error: null,
};

const warehousesSlice = createSlice({
  name: 'warehouses',
  initialState,
  reducers: {
    setWarehouses: (state, action: PayloadAction<Warehouse[]>) => {
      state.byId = {};
      state.allIds = [];
      action.payload.forEach((warehouse) => {
        state.byId[warehouse.id] = warehouse;
        state.allIds.push(warehouse.id);
      });
    },
    addWarehouse: (state, action: PayloadAction<Warehouse>) => {
      const warehouse = action.payload;
      state.byId[warehouse.id] = warehouse;
      if (!state.allIds.includes(warehouse.id)) {
        state.allIds.push(warehouse.id);
      }
    },
    updateWarehouse: (state, action: PayloadAction<Warehouse>) => {
      const warehouse = action.payload;
      state.byId[warehouse.id] = warehouse;
    },
    deleteWarehouse: (state, action: PayloadAction<string>) => {
      delete state.byId[action.payload];
      state.allIds = state.allIds.filter((id) => id !== action.payload);
    },
    setSelectedId: (state, action: PayloadAction<string | null>) => {
      state.selectedId = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<WarehousesState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
  },
});

export const {
  setWarehouses,
  addWarehouse,
  updateWarehouse,
  deleteWarehouse,
  setSelectedId,
  setFilters,
  setLoading,
  setError,
} = warehousesSlice.actions;

export default warehousesSlice.reducer;
